var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, unsafeCSS, getCompatibleStyle, adoptStyles, } from 'lit';
import { property } from 'lit/decorators/property.js';
import { THEME_CHANGE_EVENT } from './consts';
import { getUIOptions, forwardEvents, getTheme, adoptScripts, processComponentStyles, } from './methods';
import { COMMON_COLORS_TO_COMMON_GRADIENTS, VaryGroups } from './varies';
export class TiniElement extends LitElement {
    constructor() {
        super(...arguments);
        this.uiOptions = getUIOptions();
        this.activeTheme = getTheme();
        this.extraStyleAdopted = false;
        this.rootClasses = { root: true };
        this.onThemeChange = (e) => {
            this.activeTheme = e.detail;
            return this.requestUpdate();
        };
    }
    /* eslint-enable prettier/prettier */
    createRenderRoot() {
        var _a;
        const renderRoot = (_a = this.shadowRoot) !== null && _a !== void 0 ? _a : this.attachShadow(this.constructor.shadowRootOptions);
        this.customAdoptStyles(renderRoot);
        return renderRoot;
    }
    connectedCallback() {
        super.connectedCallback();
        addEventListener(THEME_CHANGE_EVENT, this.onThemeChange);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        removeEventListener(THEME_CHANGE_EVENT, this.onThemeChange);
    }
    willUpdate(changedProperties) {
        // re-style when deepStyle changed
        if (changedProperties.has('styleDeep')) {
            if (!this.extraStyleAdopted) {
                this.extraStyleAdopted = true; // skip the first time, already adopted in createRenderRoot()
            }
            else {
                this.customAdoptStyles(this.shadowRoot || this);
            }
        }
    }
    firstUpdated() {
        if (this.events)
            forwardEvents(this, this.events);
    }
    updated() {
        this.customAdoptScripts();
    }
    extendRootClasses(input) {
        const { raw = {}, pseudo = {}, overridable = {} } = input;
        const { componentOptions } = this.getGlobalOptions();
        // build pseudo info
        const pseudoInfo = Object.entries(pseudo).reduce((result, [key, value]) => {
            return !value
                ? result
                : {
                    ...result,
                    ...Object.entries(value).reduce((r, [k, v]) => {
                        if (!v)
                            return r;
                        r[`${k}-${v}-${key}`] = true;
                        return r;
                    }, {}),
                };
        }, {});
        // build overridable info
        const overridableFinalValues = {};
        const overridableInfo = Object.entries(overridable).reduce((result, [key, originalValue]) => {
            if (!originalValue)
                return result;
            const value = this.calculatePropertyValue(key, originalValue);
            overridableFinalValues[key] = value;
            result[`${key}-${value}`] = true;
            return result;
        }, {});
        // other info:
        // + refer gradient scheme on hover
        const otherInfo = {};
        const schemeValue = overridableFinalValues[VaryGroups.Scheme];
        if (componentOptions.referGradientSchemeOnHover &&
            schemeValue &&
            !~schemeValue.indexOf('gradient')) {
            const hoverScheme = COMMON_COLORS_TO_COMMON_GRADIENTS[schemeValue] || `gradient-${schemeValue}`;
            otherInfo[`${VaryGroups.Scheme}-${hoverScheme}-hover`] = true;
        }
        // result
        return (this.rootClasses = {
            ...this.rootClasses,
            ...raw,
            ...pseudoInfo,
            ...overridableInfo,
            ...otherInfo,
        });
    }
    getGlobalOptions() {
        var _a, _b, _c;
        const themeOptions = ((_a = this.uiOptions) === null || _a === void 0 ? void 0 : _a[this.activeTheme.themeId]) ||
            ((_b = this.uiOptions) === null || _b === void 0 ? void 0 : _b[this.activeTheme.soulId]) ||
            {};
        const componentOptions = ((_c = themeOptions.perComponent) === null || _c === void 0 ? void 0 : _c[this.constructor.componentName]) || {};
        return { themeOptions, componentOptions };
    }
    calculatePropertyValue(name, originalValue) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const { themeOptions } = this.getGlobalOptions();
        // no theme
        if (!this.activeTheme)
            return originalValue;
        // refers
        const camelName = name.replace(/-(\w)/g, (_, letter) => letter.toUpperCase());
        const referValue = ((_b = (_a = this.refers) === null || _a === void 0 ? void 0 : _a[this.activeTheme.themeId]) === null || _b === void 0 ? void 0 : _b[camelName]) ||
            ((_d = (_c = this.refers) === null || _c === void 0 ? void 0 : _c[this.activeTheme.themeId]) === null || _d === void 0 ? void 0 : _d[name]) ||
            ((_f = (_e = this.refers) === null || _e === void 0 ? void 0 : _e[this.activeTheme.soulId]) === null || _f === void 0 ? void 0 : _f[camelName]) ||
            ((_h = (_g = this.refers) === null || _g === void 0 ? void 0 : _g[this.activeTheme.soulId]) === null || _h === void 0 ? void 0 : _h[name]);
        if (referValue)
            return referValue;
        // refer gradient scheme
        if (!((_j = this.constructor.componentMetas) === null || _j === void 0 ? void 0 : _j.colorOnlyScheme) &&
            name === VaryGroups.Scheme &&
            themeOptions.referGradientScheme) {
            return ~originalValue.indexOf('gradient')
                ? originalValue
                : COMMON_COLORS_TO_COMMON_GRADIENTS[originalValue] || `gradient-${originalValue}`;
        }
        // default value
        return originalValue;
    }
    customAdoptStyles(renderRoot) {
        var _a;
        const allStyles = [];
        // theme styles (multiple themes, via @TiniElementTheming() only)
        const styling = (_a = this.constructor.theming) === null || _a === void 0 ? void 0 : _a.styling;
        if (styling) {
            const { soulId, themeId } = this.activeTheme;
            allStyles.push(...(styling[themeId] ||
                styling[soulId] ||
                Object.values(styling)[0] ||
                []));
        }
        // element styles
        allStyles.push(...this.constructor.elementStyles);
        // from styleDeep
        if (this.styleDeep)
            allStyles.push(this.styleDeep);
        // adopt styles
        const styleText = processComponentStyles(allStyles, this.activeTheme);
        adoptStyles(renderRoot, [
            getCompatibleStyle(unsafeCSS(styleText)),
        ]);
    }
    customAdoptScripts() {
        var _a;
        // theme scripts (multiple themes, via @TiniElementTheming() only)
        adoptScripts(this, this.activeTheme, (_a = this.constructor.theming) === null || _a === void 0 ? void 0 : _a.scripting);
    }
}
TiniElement.defaultTagName = 'tini-element';
TiniElement.componentName = 'unnamed';
TiniElement.componentMetas = {};
__decorate([
    property({ type: String, reflect: true })
], TiniElement.prototype, "styleDeep", void 0);
__decorate([
    property({ type: Object })
], TiniElement.prototype, "refers", void 0);
__decorate([
    property()
], TiniElement.prototype, "events", void 0);
//# sourceMappingURL=main.js.map